import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { useListTasks, Task } from "@/lib/api/checklist";
import { RecurrenceDisplay } from "./components";
import { Plus, Search, Eye, Edit, Users, Trash2, FileText } from "lucide-react";
import { format } from "date-fns";
import { CommonDestinationTimezone } from "@/lib/utils";
import { sortByDate } from "@/lib/freightReport";

interface TaskListProps {
  onViewTask: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onCreateInstances: (task: Task) => void;
  onCreateTask: () => void;
}

export function TaskList({
  onViewTask,
  onEditTask,
  onDeleteTask,
  onCreateInstances,
  onCreateTask,
}: TaskListProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 1000;

  const listTasksResult = useListTasks({
    page,
    limit,
    search,
    is_active: true,
  });
  
  const isLoading = listTasksResult.isLoading;
  const tasks = listTasksResult?.data?.data?.tasks || [];
  const total = listTasksResult?.data?.data?.total || 0;

  const columns: DataTableColumn<Task>[] = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => {
        const task = row.original;
        return (
          <div>
            <button
              onClick={() => onViewTask(task)}
              className="text-left font-medium hover:underline"
            >
              {task.title}
            </button>
            {task.description && (
              <p className="text-sm text-muted-foreground truncate max-w-md">
                {task.description}
              </p>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "recurrence_pattern",
      header: "Recurrence",
      cell: ({ row }) => (
        <RecurrenceDisplay
          pattern={row.original.recurrence_pattern}
          interval={row.original.recurrence_interval}
          calculationType={row.original.recurrence_calculation_type}
        />
      ),
      size: 200,
    },
    {
      accessorKey: "note",
      header: "Note",
      cell: (info) => {
        const note: string | undefined = info.getValue() as any;
        if (!note) return null;
        return (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground truncate max-w-xs" title={note}>
              {note}
            </span>
          </div>
        );
      },
      size: 200,
    },
    {
      id: "first_due",
      header: "First Due Date",
      accessorFn: (row: Task) => {
        return row.instances?.slice().sort((a: any, b: any) => sortByDate(a.due_date, b.due_date))?.[0]?.due_date ?? "";
      },
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone).format("MMM DD, YYYY") : "-",
      filterParseDateFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone) : null,
      cell: (info) => {
        const date_value = info.getValue() as string;
        if (!date_value) return "";
        return moment
          .tz(date_value, CommonDestinationTimezone)
          .format("MMM DD, YYYY");
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      columnFilterType: 'date',
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone).format("MMM DD, YYYY") : "-",
      filterParseDateFn: (v: unknown) => v ? moment.tz(v as string, CommonDestinationTimezone) : null,
      cell: (info) => {
        const date_value = info.getValue() as string;
        if (!date_value) return "";
        return moment
          .tz(date_value, CommonDestinationTimezone)
          .format("MMM DD, YYYY");
      },
      size: 120,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const task = row.original;
        return (
          <div className="flex gap-2 flex-row items-center">
            <Button
              size="icon"
              variant="outline"
              onClick={() => onViewTask(task)}
              className="h-8 w-8"
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onEditTask(task)}
              className="h-8 w-8"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onCreateInstances(task)}
              className="h-8 w-8"
              title="Assign"
            >
              <Users className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onDeleteTask(task)}
              className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive hover:border-destructive"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
      size: 200,
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[300px]"
            />
          </div>
        </div>
        <Button onClick={onCreateTask}>
          <Plus className="mr-2 h-4 w-4" />
          New Task
        </Button>
      </div>

      <DataTable
        data={tasks}
        columns={columns}
        tableClassName={"min-h-[600px] max-h-[70vh] overflow-y-auto"}
        emptyMessage={isLoading ? "Loading..." : "No data available"}
        stickyHeaders
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnVisibility
        filterStyle='select'
        filterMode='internal'
      />

      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, total)} of {total} tasks
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page * limit >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
